import { Button, Modal, PointerRight, Text, ToggleText } from '@myinterview/component-library';
import classNames from 'classnames';
import { t } from 'i18next';
import React, { useCallback, useState } from 'react';
import { emitTrackEvent, EVENT_TYPES, ITrackEvent } from '../services/event.service';
import { Header } from './Header';
import { TopTips } from './TopTips';
import { VideoQuestion } from './VideoQuestion';

interface IProps {
  introVideo: string;
  welcomeTitle?: string;
  welcomeText?: string;
  onContinue: () => void;
  logo: string;
  company: string;
  title: string;
  termsUrl?: string;
  privacyUrl?: string;
  isResumed: boolean;
  isWidgetMinimized: boolean;
}

export const WelcomePage:React.FC<IProps> = ({
  introVideo,
  welcomeTitle = t('welcome.title'),
  welcomeText,
  onContinue,
  logo,
  company,
  title,
  termsUrl = 'https://www.myinterview.com/terms/',
  privacyUrl = 'https://www.myinterview.com/privacy/',
  isResumed,
  isWidgetMinimized,
}) => {
  const [isTextOpened, setIsTextOpened] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const [isTopTipsOpened, setIsTopTipsOpened] = useState(false);

  const textRefCb = useCallback((node) => {
    setShouldShowToggle(node?.scrollHeight > node?.offsetHeight);
  }, []);

  const innerClassNames = classNames({
    'myinterview-widget-inner': true,
    'myinterview-widget-inner--welcome-page-state': true,
    'myinterview-widget-inner--no-snap': true,
    'myinterview-widget-inner--welcome-hidden': !introVideo,
  });

  const welcomeTextClassNames = classNames({
    'myinterview-widget-welcome-page__text': true,
    'myinterview-widget--rtl-support': true,
    'myinterview-widget-welcome-page__text--closed': !isTextOpened,
  });

  const openTopTips = () => {
    requestAnimationFrame(() => {
      setIsTopTipsOpened(true);
      emitTrackEvent({ eventType: EVENT_TYPES.WELCOME_STATE_TOP_TIPS });
    });
  };

  const onVideoEnds = (watchedCount: number) => {
    emitTrackEvent({ eventType: EVENT_TYPES.WELCOME_STATE_VIDEO_FULLY_WATCHED, extraData: { introVideo, watchedCount, url: introVideo } });
  };

  const onVideoStateChanged = (eventData: ITrackEvent) => {
    emitTrackEvent({
      eventType: eventData.eventType === EVENT_TYPES.VIDEO_QUESTION_PLAY ? EVENT_TYPES.WELCOME_STATE_VIDEO_PLAY : EVENT_TYPES.WELCOME_STATE_VIDEO_PAUSE,
      extraData: { ...eventData.extraData, url: introVideo },
    });
  };

  return (
    <>
      <div className={innerClassNames}>
        {introVideo && <VideoQuestion src={introVideo} isThinkingTime={false} onVideoEnds={onVideoEnds} isWidgetMinimized={isWidgetMinimized} onVideoStateChanged={onVideoStateChanged} />}
      </div>
      <div className="myinterview-widget-outer background-color--white myinterview-widget-outer--no-snap">
        <Header logo={logo || ''} companyName={company || ''} jobTitle={title || ''} forDesktopLayout />
        <div className="myinterview-widget-welcome-page myinterview-widget-scroll-indicator">
          <Text className="myinterview-widget-welcome-page__title myinterview-widget--rtl-support" size="H2-Bold">{welcomeTitle}</Text>
          <div className="myinterview-widget-welcome-page__text-wrapper">
            <Text ref={textRefCb} className={welcomeTextClassNames} size="S-Regular">{welcomeText}</Text>
            {shouldShowToggle && (
            <ToggleText
              className="myinterview-widget-welcome-page__toggle"
              hideArrow
              onToggle={setIsTextOpened}
              openedText={t('welcome.see_less')}
              closedText={t('welcome.see_more')}
            />
            )}
          </div>
          <div className="myinterview-widget-welcome-page__action-buttons">
            <Button
              className="myinterview-widget-welcome-page__continue-button"
              onClick={onContinue}
            >
              {t(isResumed ? 'welcome.resumeInterview' : 'welcome.startInterview').toUpperCase()}
            </Button>
            <Button
              className="myinterview-widget-welcome-page__top-tips-button"
              backgroundColor="white"
              color="neutral-90"
              onClick={openTopTips}
            >
              <div className="myinterview-widget-welcome-page__top-tips">
                <PointerRight />
                <span>{t('top_tips.top_tips')}</span>
              </div>
            </Button>
          </div>
          <Text className="myinterview-widget--rtl-support" size="XS-Regular" fontStyle="italic">
            <span>{t('welcome.click')}</span>
            <a className="color--special" href={termsUrl} target="_blank" rel="noreferrer">{t('welcome.terms')}</a>
            <span>{t('welcome.and')}</span>
            <a className="color--special" href={privacyUrl} target="_blank" rel="noreferrer">{t('welcome.privacy')}</a>
          </Text>
        </div>
      </div>
      <Modal
        closeButton
        clickOutside
        backgroundCover={isTopTipsOpened}
        className={isTopTipsOpened ? 'myinterview-modal-wrapper--opened' : ''}
        onClose={() => setIsTopTipsOpened(false)}
      >
        <TopTips />
      </Modal>
    </>
  );
};
